import React, { useContext } from 'react';
import {
  Users, FolderKanban, Calculator, ShoppingCart, Truck, Video, ShieldCheck,
  Megaphone, BookOpen, BookOpenText, Briefcase, GraduationCap, Send, Tag, FileText, Sparkles, CalendarDays,
} from 'lucide-react';
import SectionHubLayout from '@/components/SectionHubLayout';
import useNotificationCounts from '@/hooks/useNotificationCounts';
import AuthContext from '@/contexts/AuthContext';

const BRAND = {
  name: 'ProEpoxy',
  fullName: 'Ox Resin ProEpoxy',
  tagline: 'Epoxy Flooring Systems',
  primaryColor: '#7A0B7A',
  secondaryColor: '#300033',
  sectionKey: 'proepoxy',
  logo: 'https://customer-assets.emergentagent.com/job_76625416-d43e-411a-b4d0-3b73c58f517c/artifacts/ta6bz3y6_IMG_8292.jpeg',
};

const CATEGORIES = [
  {
    id: 'leads-marketing',
    title: 'Leads & Marketing',
    description: 'Win new work and promote your business',
    icon: Megaphone,
    tiles: [
      { id: 'lead-offers', title: 'Lead Offers', description: 'New ProEpoxy leads offered to you', icon: Send, path: '/lead-offers?section=proepoxy', notifKey: 'offer' },
      { id: 'leads', title: 'Lead Management', description: 'Track ProEpoxy enquiries through to project', icon: Users, path: '/proepoxy/leads', notifKey: 'lead' },
    ],
  },
  {
    id: 'quotes-projects',
    title: 'Quotes & Project Management',
    description: 'Quote, order and deliver',
    icon: Briefcase,
    tiles: [
      { id: 'quote-project', title: 'Customer Quotes', description: 'Customer-facing PDF quotes (coming soon)', icon: Calculator, path: null },
      { id: 'my-orders', title: 'Material Orders', description: 'Build new orders, view pricing & track delivery', icon: Truck, path: '/proepoxy/my-orders', notifKey: 'order' },
      { id: 'projects', title: 'Project Management', description: 'Track active ProEpoxy projects', icon: FolderKanban, path: '/proepoxy/projects' },
      { id: 'calendar', title: 'Project Calendar', description: 'Plan your weeks — allocate install stages to days across all jobs', icon: CalendarDays, path: '/proepoxy/calendar' },
    ],
  },
  {
    id: 'training-development',
    title: 'Training & Professional Development',
    description: 'Learn the craft and earn the badge',
    icon: GraduationCap,
    tiles: [
      { id: 'videos', title: 'Training Videos', description: 'Tips, tricks & install techniques', icon: Video, path: '/videos/proepoxy' },
      { id: 'course-notes', title: 'Course Notes & Training Material', description: 'Course handbook PDF · download or read inline', icon: BookOpenText, path: '/notes/proepoxy' },
      { id: 'get-approved', title: 'Become OxApproved', description: 'Track your ProEpoxy approval status', icon: ShieldCheck, path: '/approval/proepoxy' },
    ],
  },
  {
    id: 'toolbox',
    title: 'Toolbox',
    description: 'Marketing kit, calculators & letter templates',
    icon: BookOpen,
    tiles: [
      { id: 'marketing', title: 'Marketing Material', description: 'Branded leaflets, posters, social assets', icon: BookOpen, path: '/marketing-materials?section=proepoxy' },
      { id: 'calculators', title: 'Calculators', description: 'Self-smoothing compound calculator & useful docs', icon: Calculator, path: '/toolbox?brand=proepoxy' },
      { id: 'templates', title: 'Templates', description: 'Quote letters, RAMS, customer emails, T&Cs, warranty & aftercare', icon: FileText, path: '/templates?brand=proepoxy' },
    ],
  },
];

const ProEpoxyHub = () => {
  const { bySectionCategory: notifBSC } = useNotificationCounts();
  const { user } = useContext(AuthContext) || {};
  const isStaff = ['admin', 'staff', 'super_admin'].includes(user?.role);

  // Staff-only: expose the Recipe Catalogue admin as an extra tile under
  // Quotes & Project Management. Non-staff never see this tile.
  const categories = React.useMemo(() => {
    if (!isStaff) return CATEGORIES;
    return CATEGORIES.map((cat) => {
      if (cat.id !== 'quotes-projects') return cat;
      return {
        ...cat,
        tiles: [
          ...cat.tiles,
          {
            id: 'recipes',
            title: 'Signature Recipes',
            description: 'Curate one-tap presets that pre-fill the Metallic calculator',
            icon: Sparkles,
            path: '/staff/proepoxy/recipes',
          },
        ],
      };
    });
  }, [isStaff]);

  return (
    <SectionHubLayout
      brand={BRAND}
      categories={categories}
      testIdPrefix="proepoxy"
      notifBSC={notifBSC}
    />
  );
};

export default ProEpoxyHub;
